/**
 * Centralized Error Handling Utilities
 * 
 * Standardized error handling across the entire application.
 * Integrates with:
 * - logger service (structured logging)
 * - errorHandlingService (error categorization)
 * - NotificationSystem (user feedback)
 * - ErrorBoundary (React error boundaries)
 * 
 * Usage:
 *   import { handleError, safeExecute, withErrorHandling } from '../utils/errorUtils';
 * 
 *   // Simple error handling
 *   try {
 *     await someOperation();
 *   } catch (error) {
 *     handleError(error, { context: 'OperationName', userId: 'user123' });
 *   }
 * 
 *   // Safe execution (returns result or null)
 *   const result = await safeExecute(() => someOperation());
 * 
 *   // With retry and user feedback
 *   const result = await withErrorHandling(
 *     () => someOperation(),
 *     { errorMessage: 'Failed to save', retryCount: 2 }
 *   );
 */

import { logger } from '../services/logger';
import { errorHandler, ErrorDetails } from '../services/errorHandlingService';
import { showGlobalError, showGlobalWarning, showGlobalSuccess } from '../components/NotificationSystem';

const log = logger.createScope('ErrorUtils');

/**
 * Error handling context for better debugging
 */
export interface ErrorContext {
  operation?: string;
  userId?: string;
  petId?: string;
  documentId?: string;
  fileName?: string;
  category?: string;
  [key: string]: unknown;
}

/**
 * Error handling options
 */
export interface ErrorHandlingOptions {
  /**
   * Whether to show error to user (default: true)
   */
  showToUser?: boolean;
  
  /**
   * Custom user-friendly error message
   */
  userMessage?: string;
  
  /**
   * Whether to log error (default: true)
   */
  logError?: boolean;
  
  /**
   * Log level: 'error' | 'warn' | 'debug' (default: 'error')
   */
  logLevel?: 'error' | 'warn' | 'debug';
  
  /**
   * Whether to throw error after handling (default: false)
   */
  rethrow?: boolean;
  
  /**
   * Additional context for logging
   */
  context?: ErrorContext;
  
  /**
   * Whether error is retryable (auto-detected from ErrorDetails)
   */
  retryable?: boolean;
}

/**
 * Standardized error handling function
 * 
 * This is the main function to use for all error handling across the app.
 * It:
 * 1. Categorizes the error (Firebase, network, validation, etc.)
 * 2. Logs the error with context
 * 3. Shows user-friendly message if needed
 * 4. Returns error details for further processing
 * 
 * @param error - The error to handle
 * @param options - Error handling options
 * @returns ErrorDetails object with categorized error information
 */
export function handleError(
  error: unknown,
  options: ErrorHandlingOptions = {}
): ErrorDetails {
  const {
    showToUser = true,
    userMessage,
    logError = true,
    logLevel = 'error',
    rethrow = false,
    context = {},
  } = options;

  // Convert unknown error to Error object
  const errorObj = error instanceof Error ? error : new Error(String(error));

  // Get categorized error details from errorHandlingService
  const errorDetails = errorHandler.handleError(errorObj);

  // Log the error with context
  if (logError) {
    const logContext = {
      ...context,
      errorCode: errorDetails.code,
      severity: errorDetails.severity,
      category: errorDetails.category,
      retryable: errorDetails.retryable,
      operation: context.operation || 'Unknown',
    };

    switch (logLevel) {
      case 'warn':
        log.warn(errorDetails.message, logContext);
        break;
      case 'debug':
        log.debug(errorDetails.message, logContext);
        break;
      case 'error':
      default:
        log.error(errorDetails.message, errorObj, logContext);
        break;
    }
  }

  // Show user-friendly message if needed
  if (showToUser && errorHandler.shouldShowToUser(errorDetails)) {
    const message = userMessage || errorDetails.userMessage;
    showGlobalError(message, 'Error', errorObj.stack);
  }

  // Re-throw if requested
  if (rethrow) {
    throw errorObj;
  }

  return errorDetails;
}

/**
 * Safe execution wrapper - catches errors and returns null instead of throwing
 * 
 * Use this when you want to gracefully handle errors without try-catch blocks.
 * 
 * @param operation - Function to execute
 * @param options - Error handling options
 * @returns Result of operation or null if error occurred
 */
export async function safeExecute<T>(
  operation: () => Promise<T>,
  options: ErrorHandlingOptions = {}
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    handleError(error, {
      ...options,
      showToUser: false, // Don't show UI for safe operations
      rethrow: false,
    });
    return null;
  }
}

/**
 * Safe synchronous execution wrapper
 */
export function safeExecuteSync<T>(
  operation: () => T,
  options: ErrorHandlingOptions = {}
): T | null {
  try {
    return operation();
  } catch (error) {
    handleError(error, {
      ...options,
      showToUser: false,
      rethrow: false,
    });
    return null;
  }
}

/**
 * Async operation wrapper with retry logic and user feedback
 * 
 * This is an enhanced version of the existing withErrorHandling that integrates
 * with the centralized error handling system.
 * 
 * @param operation - Async function to execute
 * @param options - Operation options
 * @returns Result object with success status and data/error
 */
export interface AsyncOperationOptions {
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  suppressErrorUI?: boolean;
  retryCount?: number;
  retryDelay?: number;
  context?: ErrorContext;
}

export interface AsyncOperationResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  errorDetails?: ErrorDetails;
  retryCount: number;
}

export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  options: AsyncOperationOptions = {}
): Promise<AsyncOperationResult<T>> {
  const {
    loadingMessage,
    successMessage,
    errorMessage,
    suppressErrorUI = false,
    retryCount = 0,
    context = {},
  } = options;

  let lastError: Error | null = null;
  let lastErrorDetails: ErrorDetails | null = null;
  let attempt = 0;
  const maxAttempts = retryCount + 1;

  // Show loading message if provided
  if (loadingMessage) {
    showGlobalWarning(loadingMessage, 'Loading...');
  }

  while (attempt < maxAttempts) {
    try {
      const result = await operation();
      
      // Show success message if provided
      if (successMessage) {
        showGlobalSuccess(successMessage);
      }
      
      return {
        success: true,
        data: result,
        retryCount: attempt,
      };
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      attempt++;
      
      // Get error details from error handler
      lastErrorDetails = errorHandler.handleError(lastError);
      
      log.warn(`Operation failed (attempt ${attempt}/${maxAttempts})`, {
        ...context,
        error: lastError,
        attempt,
        maxAttempts,
        errorCode: lastErrorDetails.code,
        retryable: lastErrorDetails.retryable,
      });
      
      // If we have more retries left and error is retryable, wait and try again
      if (attempt < maxAttempts && lastErrorDetails.retryable) {
        const delay = errorHandler.getRetryDelay(lastErrorDetails, attempt - 1);
        log.debug(`Retrying in ${delay}ms...`, {
          ...context,
          attempt,
          delay,
        });
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // All retries exhausted or error not retryable, handle the error
      if (!suppressErrorUI) {
        const finalErrorMessage = errorMessage || lastErrorDetails.userMessage;
        handleError(lastError, {
          showToUser: true,
          userMessage: finalErrorMessage,
          context,
        });
      } else {
        // Still log even if UI is suppressed
        handleError(lastError, {
          showToUser: false,
          context,
        });
      }
      
      return {
        success: false,
        error: lastError,
        errorDetails: lastErrorDetails,
        retryCount: attempt - 1,
      };
    }
  }

  // This should never be reached, but TypeScript requires it
  return {
    success: false,
    error: lastError || new Error('Unknown error'),
    errorDetails: lastErrorDetails || undefined,
    retryCount: attempt - 1,
  };
}

/**
 * Create a standardized error handler for a specific operation
 * 
 * This creates a reusable error handler with pre-configured context.
 * 
 * @param operationName - Name of the operation (for logging)
 * @param defaultOptions - Default options for this operation
 * @returns Error handler function
 */
export function createErrorHandler(
  operationName: string,
  defaultOptions: ErrorHandlingOptions = {}
) {
  return (error: unknown, options: ErrorHandlingOptions = {}) => {
    return handleError(error, {
      ...defaultOptions,
      ...options,
      context: {
        operation: operationName,
        ...defaultOptions.context,
        ...options.context,
      },
    });
  };
}

/**
 * Wrap a service function with standardized error handling
 * 
 * This decorator automatically handles errors for service functions.
 * 
 * @param serviceFunction - The service function to wrap
 * @param operationName - Name of the operation
 * @param options - Error handling options
 * @returns Wrapped function with error handling
 */
export function withServiceErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  serviceFunction: T,
  operationName: string,
  options: ErrorHandlingOptions = {}
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await serviceFunction(...args);
    } catch (error) {
      const errorDetails = handleError(error, {
        ...options,
        context: {
          operation: operationName,
          ...options.context,
        },
      });
      
      // Re-throw with user-friendly message if configured
      if (options.rethrow) {
        throw new Error(errorDetails.userMessage);
      }
      
      throw error;
    }
  }) as T;
}

/**
 * Get user-friendly error message from any error
 * 
 * @param error - The error object
 * @returns User-friendly error message
 */
export function getUserFriendlyMessage(error: unknown): string {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const errorDetails = errorHandler.handleError(errorObj);
  return errorDetails.userMessage;
}

/**
 * Check if an error is retryable
 * 
 * @param error - The error object
 * @returns Whether the error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const errorDetails = errorHandler.handleError(errorObj);
  return errorDetails.retryable;
}

/**
 * Get retry delay for an error (with exponential backoff)
 * 
 * @param error - The error object
 * @param attempt - Current attempt number (0-indexed)
 * @returns Retry delay in milliseconds
 */
export function getRetryDelay(error: unknown, attempt: number): number {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const errorDetails = errorHandler.handleError(errorObj);
  return errorHandler.getRetryDelay(errorDetails, attempt);
}

/**
 * React hook for error handling in components
 * 
 * Usage:
 *   const { handleError, getUserMessage, isRetryable } = useErrorHandling();
 * 
 *   try {
 *     await someOperation();
 *   } catch (error) {
 *     handleError(error, { context: { operation: 'SavePet' } });
 *   }
 */
export function useErrorHandling() {
  return {
    handleError: (error: unknown, options?: ErrorHandlingOptions) => 
      handleError(error, options),
    getUserMessage: (error: unknown) => getUserFriendlyMessage(error),
    isRetryable: (error: unknown) => isRetryableError(error),
    getRetryDelay: (error: unknown, attempt: number) => getRetryDelay(error, attempt),
    safeExecute: <T>(operation: () => Promise<T>) => safeExecute(operation),
    withErrorHandling: <T>(
      operation: () => Promise<T>,
      options?: AsyncOperationOptions
    ) => withErrorHandling(operation, options),
  };
}

/**
 * Common error handlers for specific operations
 * 
 * Pre-configured error handlers for common operations.
 */
export const commonErrorHandlers = {
  /**
   * Handle pet service errors
   */
  pet: {
    save: (error: unknown) => handleError(error, {
      userMessage: 'Failed to save pet information. Please check all fields and try again.',
      context: { operation: 'PetSave' },
    }),
    delete: (error: unknown) => handleError(error, {
      userMessage: 'Failed to delete pet. Please try again.',
      context: { operation: 'PetDelete' },
    }),
    fetch: (error: unknown) => handleError(error, {
      userMessage: 'Failed to load pets. Please refresh the page.',
      context: { operation: 'PetFetch' },
    }),
  },

  /**
   * Handle document service errors
   */
  document: {
    upload: (error: unknown) => handleError(error, {
      userMessage: 'File upload failed. Please check your internet connection and try again.',
      context: { operation: 'DocumentUpload' },
      retryable: true,
    }),
    process: (error: unknown) => handleError(error, {
      userMessage: 'Failed to process document. Please ensure the file is valid.',
      context: { operation: 'DocumentProcess' },
    }),
    delete: (error: unknown) => handleError(error, {
      userMessage: 'Failed to delete document. Please try again.',
      context: { operation: 'DocumentDelete' },
    }),
  },

  /**
   * Handle lab processing errors
   */
  lab: {
    upload: (error: unknown) => handleError(error, {
      userMessage: 'Failed to process lab results. Please ensure the file is a valid lab report.',
      context: { operation: 'LabUpload' },
    }),
    process: (error: unknown) => handleError(error, {
      userMessage: 'Failed to analyze lab results. Please try again.',
      context: { operation: 'LabProcess' },
    }),
  },

  /**
   * Handle appointment errors
   */
  appointment: {
    save: (error: unknown) => handleError(error, {
      userMessage: 'Failed to save appointment. Please check the date and time and try again.',
      context: { operation: 'AppointmentSave' },
    }),
    delete: (error: unknown) => handleError(error, {
      userMessage: 'Failed to delete appointment. Please try again.',
      context: { operation: 'AppointmentDelete' },
    }),
  },

  /**
   * Handle authentication errors
   */
  auth: {
    login: (error: unknown) => handleError(error, {
      userMessage: 'Login failed. Please check your credentials and try again.',
      context: { operation: 'AuthLogin' },
      retryable: true,
    }),
    signup: (error: unknown) => handleError(error, {
      userMessage: 'Sign up failed. Please check your information and try again.',
      context: { operation: 'AuthSignup' },
    }),
  },
};

/**
 * Default export with all utilities
 */
const errorUtils = {
  handleError,
  safeExecute,
  safeExecuteSync,
  withErrorHandling,
  createErrorHandler,
  withServiceErrorHandling,
  getUserFriendlyMessage,
  isRetryableError,
  getRetryDelay,
  useErrorHandling,
  commonErrorHandlers,
};

export default errorUtils;

